import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy() {
  return (
    <div className="font-['Outfit'] bg-[#0A0A14] text-gray-50 min-h-screen">
      <Helmet>
        <title>Privacy Policy - FocusDen App Blocker</title>
        <meta name="description" content="FocusDen Privacy Policy - Learn how we protect your data and privacy. 100% private, no data collection, all data stays on your device." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.focusdenapp.com/privacy" />
      </Helmet>

      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute w-[500px] h-[500px] -top-[10%] -left-[10%] rounded-full blur-[100px] bg-[radial-gradient(circle,_rgba(99,102,241,0.2)_0%,_transparent_70%)]" />
        <div className="absolute w-[400px] h-[400px] top-[50%] -right-[5%] rounded-full blur-[100px] bg-[radial-gradient(circle,_rgba(236,72,153,0.15)_0%,_transparent_70%)]" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 md:px-8 py-4 backdrop-blur-xl bg-[#0A0A14]/95 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <a href="/" className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/transparent_logo.png`}
              alt="FocusDen logo"
              className="w-10 h-10"
            />
            <span className="text-2xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-500 bg-clip-text text-transparent">
              FocusDen
            </span>
          </a>
          <a href="/" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">
            Back to Home
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pt-32 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block bg-indigo-500/10 border border-indigo-500/20 px-4 py-2 rounded-full text-sm text-indigo-300 mb-4">
              📜 Legal
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-400">
              Last Updated: January 29, 2026
            </p>
          </div>

          {/* Privacy Promise */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-3xl p-8 mb-12">
            <div className="flex items-start gap-4">
              <span className="text-5xl">🔒</span>
              <div>
                <h2 className="text-2xl font-bold mb-3 text-green-400">Our Privacy Promise</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  <strong>Your privacy is our top priority.</strong> FocusDen is designed with privacy-first principles:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span><strong>100% Local Storage:</strong> All your data stays on your device</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span><strong>Zero Data Collection:</strong> We don't collect, store, or sell any personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span><strong>No Account Required:</strong> Use FocusDen without creating an account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span><strong>No Third-Party Tracking:</strong> We don't use analytics or tracking tools</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-200">1.1 Data Stored Locally on Your Device</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                FocusDen stores the following information locally on your Android device:
              </p>
              <ul className="space-y-2 text-gray-400 mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>Focus Tasks:</strong> Task names, scheduled times, selected days, and blocked apps</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>App Blocking Data:</strong> List of apps you choose to block</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>Usage Statistics:</strong> Block attempts, focus streaks, productivity scores (stored locally only)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>Settings & Preferences:</strong> Your app settings, strict mode preferences, and customizations</span>
                </li>
              </ul>
              <p className="text-gray-400 leading-relaxed">
                <strong className="text-white">Important:</strong> This data NEVER leaves your device. It is not uploaded to our servers, shared with third parties, or accessible to anyone but you.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-gray-200">1.2 Data We Do NOT Collect</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                We explicitly do NOT collect:
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Personal identification information (name, email, phone number)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Location data or GPS coordinates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Contacts, photos, or media files</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Content from other apps (messages, emails, browsing history)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Device identifiers for advertising purposes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Analytics or usage tracking data</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">2. Android Permissions Explained</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                FocusDen requires certain Android permissions to function. Here's exactly what each permission is used for:
              </p>

              <div className="space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-2 text-indigo-400">🔍 Accessibility Service</h3>
                  <p className="text-gray-400 leading-relaxed mb-2">
                    <strong>Purpose:</strong> Detect when you open a blocked app and display the blocker screen.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does:</strong> Monitors which app is currently in the foreground (on your screen).
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does NOT do:</strong> Does not read content from apps, access your messages, or collect any personal data.
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-2 text-indigo-400">🔔 Notification Access (Premium Only)</h3>
                  <p className="text-gray-400 leading-relaxed mb-2">
                    <strong>Purpose:</strong> Block notifications from distracting apps during focus sessions.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does:</strong> Temporarily suppresses notifications from apps you've selected.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does NOT do:</strong> Does not read notification content or share it with anyone.
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-2 text-indigo-400">🛡️ Device Admin (Premium Only)</h3>
                  <p className="text-gray-400 leading-relaxed mb-2">
                    <strong>Purpose:</strong> Prevent uninstalling FocusDen when Strict Mode is enabled.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does:</strong> Requires you to complete your chosen challenge before uninstalling.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does NOT do:</strong> Does not lock your device or access any personal data.
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-2 text-indigo-400">⏰ Alarm & Reminders</h3>
                  <p className="text-gray-400 leading-relaxed mb-2">
                    <strong>Purpose:</strong> Schedule focus tasks to start and stop automatically.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does:</strong> Sets alarms to trigger app blocking at your scheduled times.
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-2 text-indigo-400">🔋 Battery Optimization Exemption</h3>
                  <p className="text-gray-400 leading-relaxed mb-2">
                    <strong>Purpose:</strong> Ensure FocusDen works reliably in the background.
                  </p>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    <strong>What it does:</strong> Prevents Android from killing the app to save battery during focus sessions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">3. How We Use Your Information</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Since all your data stays on your device, we don't "use" your information in any traditional sense. The data you create is solely for your benefit:
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>To block apps during your scheduled focus times</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>To display your productivity statistics and streaks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>To remember your preferences and settings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span>To provide you with an uninterrupted focus experience</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">4. Data Sharing & Third Parties</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                <strong className="text-white">We do NOT share your data with anyone. Period.</strong>
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                FocusDen does not:
              </p>
              <ul className="space-y-2 text-gray-400 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Sell or rent your personal information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Share data with advertisers or marketing companies</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Use third-party analytics tools (like Google Analytics)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Integrate with social media platforms for tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Provide data to government agencies (unless legally required by court order)</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-gray-200">Payment Processing (Premium Only)</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                When you purchase FocusDen Premium through Google Play Store, the payment is processed entirely by Google. We do not collect or store:
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Credit card or payment information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Billing addresses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">✗</span>
                  <span>Transaction details beyond what Google Play provides for verification</span>
                </li>
              </ul>
              <p className="text-gray-400 leading-relaxed text-sm mt-4">
                Refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">Google's Privacy Policy</a> for information about how they handle payments.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">5. Data Security</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Your data is secure because it never leaves your device:
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Local Encryption:</strong> All data is stored in your device's secure internal storage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>No Cloud Sync:</strong> No risk of server breaches or data leaks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Protected by Android:</strong> Your device's built-in security protects your data</span>
                </li>
              </ul>
              <p className="text-gray-400 leading-relaxed mt-4">
                If you uninstall FocusDen, all data is permanently deleted from your device.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">6. Children's Privacy</h2>
              <p className="text-gray-400 leading-relaxed">
                FocusDen is suitable for users of all ages. Since we don't collect any personal information, there is no data collection from children under 13. Parents can safely allow their children to use FocusDen without privacy concerns.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">7. Your Rights & Control</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                You have complete control over your data:
              </p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>View Your Data:</strong> All your data is visible in the app's analytics and settings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>Delete Your Data:</strong> Clear all data from Settings → Clear All Data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>Revoke Permissions:</strong> Remove any permission via Android Settings → Apps → FocusDen → Permissions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  <span><strong>Uninstall:</strong> Uninstalling the app permanently deletes all data</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">8. Changes to This Policy</h2>
              <p className="text-gray-400 leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We will notify users of significant changes through an in-app notification. Your continued use of FocusDen after changes are posted constitutes acceptance of those changes.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-6">
              <h2 className="text-2xl font-bold mb-4 text-white">9. International Users</h2>
              <p className="text-gray-400 leading-relaxed">
                FocusDen is available worldwide. Since all data is stored locally on your device, no international data transfers occur. Your data stays in your country, on your device.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <h2 className="text-2xl font-bold mb-4 text-white">10. Contact Us</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy or how FocusDen works, please contact us:
              </p>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-2">
                <p className="text-gray-300">
                  <strong>Email:</strong> <a href="mailto:privacy@focusdenapp.com" className="text-indigo-400 hover:text-indigo-300 underline">privacy@focusdenapp.com</a>
                </p>
                <p className="text-gray-300">
                  <strong>Support:</strong> <a href="mailto:support@focusdenapp.com" className="text-indigo-400 hover:text-indigo-300 underline">support@focusdenapp.com</a>
                </p>
                <p className="text-gray-300">
                  <strong>Website:</strong> <a href="https://www.focusdenapp.com" className="text-indigo-400 hover:text-indigo-300 underline">www.focusdenapp.com</a>
                </p>
              </div>
              <p className="text-gray-400 leading-relaxed text-sm mt-4">
                We typically respond to inquiries within 48 hours.
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center bg-gradient-to-br from-indigo-500/10 to-purple-500/5 border border-indigo-500/20 rounded-3xl p-12">
            <h2 className="text-3xl font-bold mb-4">Questions About Your Privacy?</h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              We're here to help. If you have any concerns about how FocusDen handles your data, don't hesitate to reach out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:official.supnistic.ai@gmail.com" 
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-8 py-4 rounded-2xl font-semibold hover:scale-105 transition-transform"
              >
                📧 Contact Privacy Team
              </a>
              <a 
                href="/" 
                className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl font-medium hover:bg-white/10 transition-all"
              >
                ← Back to Home
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-4 md:px-8 py-12 relative z-10">
        <div className="max-w-6xl mx-auto text-center text-gray-500 text-sm">
          <p className="mb-2">© 2026 FocusDen. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-6 text-xs">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/contact" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}